/**
 * Currency formatting utilities.
 *
 * Iranian Toman is the de-facto display currency (Rial / 10). The
 * backend stores amounts as integer Toman. We never do currency math
 * client-side — we just format the integer for display.
 *
 * `Intl.NumberFormat` handles locale-aware digit shaping and grouping
 * for free:
 *   fa-IR  → "۲۵۰٬۰۰۰"  (Persian digits + ٬ as thousand separator)
 *   en-US  → "250,000"  (Latin digits + , as thousand separator)
 *
 * We append the unit word ("تومان" / "Toman") as plain text because
 * Toman isn't an ISO 4217 currency — Intl's `currency` style would
 * fall back to IRR rial with the wrong unit.
 */

/**
 * Format an integer Toman amount for display.
 *
 * @param {number|string|null|undefined} amount  Integer Toman from backend.
 *   Non-numeric input (null/undefined/NaN) returns an empty string so
 *   callers can use the result directly in JSX without falsy guards.
 * @param {string} language  'fa' or 'en' (from Redux selectLanguage).
 *   Other values fall back to 'en' formatting.
 * @returns {string} formatted amount with unit, e.g. "۲۵۰٬۰۰۰ تومان"
 *
 * @example
 *   formatToman(250000, 'fa')  // → "۲۵۰٬۰۰۰ تومان"
 *   formatToman(250000, 'en')  // → "250,000 Toman"
 *   formatToman(null,   'fa')  // → ""
 */
export function formatToman(amount, language) {
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(num)) return '';

  const locale = language === 'fa' ? 'fa-IR' : 'en-US';
  // maximumFractionDigits: 0 because Toman amounts are always whole.
  // useGrouping: 'always' is the default but stating it for clarity.
  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(num);

  const unit = language === 'fa' ? 'تومان' : 'Toman';
  return `${formatted} ${unit}`;
}

export default formatToman;

/**
 * VAT rate applied to services. MUST stay in sync with the backend
 * (mock uses the same 0.10 in servicesApi.mock.checkoutService). The
 * server's proforma is authoritative; this constant only powers the
 * client-side *preview* shown before the user commits to paying.
 * 10% is Iran's actual VAT rate.
 */
export const VAT_RATE = 0.10;

/**
 * Compute a proforma breakdown from a base Toman amount, client-side.
 *
 * Used by CheckoutPage to show the price breakdown immediately, before
 * the user has entered a clinic code (the real proforma comes back from
 * `checkoutService` at pay time and should be preferred once available).
 *
 * @param {number} baseAmount  Integer Toman.
 * @returns {{ baseAmount: number, discount: number, tax: number, totalAmount: number }}
 *
 * @example
 *   computeProforma(250000)  // → { baseAmount: 250000, discount: 0, tax: 22500, totalAmount: 272500 }
 */
export function computeProforma(baseAmount) {
  const base = Number(baseAmount) || 0;
  const tax = Math.round(base * VAT_RATE);
  return { baseAmount: base, discount: 0, tax, totalAmount: base + tax };
}
